import { Link } from "react-router-dom";
import { formatDate } from "../api";
import { useClientSelection } from "../client-context";
import { useApi } from "../hooks";
import { EmptyState, ErrorState, PageHeader, PageSkeleton } from "../components/Ui";
import { NoClient } from "../components/Workflow";
import "../polish/compare.css";

type Metric = { key: string; label: string; unit: "pct" | "usd" | "count" | "stars" | "score"; better: "high" | "low"; value: number | null; peer_median: number | null; rank: number | null; of: number };
type Peer = { id: number; name: string } & Record<string, number | null | string>;
type Data = { client: { id: number; name: string; industry: string | null }; period: { from: string; to: string }; peers: Peer[]; metrics: Metric[]; notes: string[] };

export function fmt(v: number | null | undefined, unit: Metric["unit"]): string {
  if (v == null) return "—";
  if (unit === "pct") return `${v.toLocaleString("en-US", { maximumFractionDigits: 1 })}%`;
  if (unit === "usd") return `$${Math.round(v).toLocaleString("en-US")}`;
  if (unit === "stars") return v.toFixed(1);
  return Math.round(v).toLocaleString("en-US");
}
/** Ahead, behind or level with the peer median, in the direction that is better for this figure. */
export function standing(m: Pick<Metric, "value" | "peer_median" | "better">): "ahead" | "behind" | "level" | null {
  if (m.value == null || m.peer_median == null) return null;
  if (m.value === m.peer_median) return "level";
  return (m.better === "high" ? m.value > m.peer_median : m.value < m.peer_median) ? "ahead" : "behind";
}
const WORDS = { ahead: "Ahead of peers", behind: "Behind peers", level: "Level with peers" };

/**
 * A client next to the other clients in its industry (the Junk Kings, the Five Star Paintings),
 * last 30 days: where it is ahead, where it is behind, and which peer is doing best, so what
 * works for one franchise can be copied to another.
 */
export default function Compare() {
  const { clientId, client } = useClientSelection();
  return <>
    <PageHeader title="Compare to peers" description={clientId ? `${client?.name || "This client"} next to the other clients in its industry, last 30 days.` : "Pick a client to compare it with its peers."} />
    {clientId ? <CompareView key={clientId} clientId={clientId} /> : <NoClient />}
  </>;
}

function CompareView({ clientId }: { clientId: number }) {
  const { data, loading, error, reload } = useApi<Data>(`/api/compare/${clientId}`);
  if (loading && !data) return <PageSkeleton cards={2} />;
  if (error || !data) return <ErrorState message={error || "The comparison could not load."} onRetry={reload} />;
  if (!data.peers.length) return <section className="panel"><EmptyState title="No peers yet" description={data.notes[0] || "No other active client shares this industry."} /></section>;
  const best = (m: Metric) => {
    const ranked = data.peers.filter((p) => p[m.key] != null) as Peer[];
    if (!ranked.length) return null;
    return ranked.reduce((a, b) => ((m.better === "high" ? Number(b[m.key]) > Number(a[m.key]) : Number(b[m.key]) < Number(a[m.key])) ? b : a));
  };
  const shown = data.metrics;
  return (
    <div className="compare">
      <p className="muted compare-period">{data.peers.length} {data.peers.length === 1 ? "peer" : "peers"} in {data.client.industry}, {formatDate(data.period.from)} to {formatDate(data.period.to)}</p>
      <section className="panel">
        <div className="panel-heading"><div><h2>Where {data.client.name} stands</h2>
          <p className="compare-note">Rank 1 is the best among the clients that have that figure. A dash means there is not enough data yet.</p></div></div>
        <div className="table-wrap">
          <table className="data-table compare-table">
            <thead><tr><th>Figure</th><th className="num">This client</th><th className="num">Peer median</th><th className="num">Rank</th><th>Best peer</th></tr></thead>
            <tbody>{shown.map((m) => {
              const s = standing(m); const b = best(m);
              return (
                <tr key={m.key}>
                  <td data-label="Figure">{m.label}{s && <span className={`compare-chip is-${s}`}>{WORDS[s]}</span>}</td>
                  <td className="num" data-label="This client"><strong>{fmt(m.value, m.unit)}</strong></td>
                  <td className="num" data-label="Peer median">{fmt(m.peer_median, m.unit)}</td>
                  <td className="num" data-label="Rank">{m.rank ? `${m.rank} of ${m.of}` : "—"}</td>
                  {/* When this client is first, the best peer is behind it: say so instead of naming a worse figure as "best". */}
                  {/* One span: on a phone each loose piece was a grid item of its own, and the figure dropped to a line by itself. */}
                  <td data-label="Best peer"><span className="compare-best">{m.rank === 1
                    ? <><strong>This client</strong>{b && <span className="muted compare-next"> Next: <Link to={`/compare?client_id=${b.id}`}>{b.name}</Link> {fmt(Number(b[m.key]), m.unit)}</span>}</>
                    : b ? <><Link to={`/compare?client_id=${b.id}`}>{b.name}</Link> <span className="muted">{fmt(Number(b[m.key]), m.unit)}</span></> : "—"}</span></td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      </section>
      <section className="panel">
        <div className="panel-heading"><div><h2>Every peer</h2></div></div>
        <div className="table-wrap">
          <table className="data-table compare-peers">
            <thead><tr><th>Client</th>{shown.map((m) => <th key={m.key} className="num">{m.label}</th>)}</tr></thead>
            <tbody>{data.peers.map((p) => (
              <tr key={p.id}><td data-label="Client"><Link to={`/compare?client_id=${p.id}`}>{p.name}</Link></td>
                {shown.map((m) => <td key={m.key} className="num" data-label={m.label} data-empty={p[m.key] == null || undefined}>{fmt(p[m.key] as number | null, m.unit)}</td>)}</tr>
            ))}</tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
